class SlideshowTabs extends HTMLElement {
  constructor() {
    super();

    this.CLASSES = {
      active: 'active',
      loading: 'loading',
    };

    this.SELECTORS = {
      slideshowThumbsMobile: '.js-slideshow-thumbs',
      slideshowCarouselMobile: '.js-slideshow-carousel',
      tabHeaderDesktop: '[data-tab-header-desktop]',
      tabHeaderMobile: '[data-tab-header-mobile]',
      tabPanel: '[data-tab-panel]',
      desktopTabHeaderContainer: '.js-desktop-tab-header-container',
    };

    this.tabs = [];
    this.autoplayTimer = null;
    this.boundHandleMobileTabClick = this.handleMobileTabClick.bind(this);
  }

  connectedCallback() {
    this.initTabs();
  }

  disconnectedCallback() {
    this.clearAutoplayTimer();
    this.destroyPanelEvents();
    this.removeEventListener('click', this.boundHandleMobileTabClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {}

  initTabs() {
    this.tabs = Array.from(this.querySelectorAll(this.SELECTORS.tabHeaderDesktop)).map((tab) => {
      const tabId = tab.dataset.tabId;
      const tabPanel = this.querySelector(`${this.SELECTORS.tabPanel}[data-tab-id="${tabId}"]`);

      return {
        tabId: tabId,
        tabPanel: tabPanel,
        tabHeaderDesktop: tab,
        tabHeaderMobile: this.querySelector(`${this.SELECTORS.tabHeaderMobile}[data-tab-id="${tabId}"]`),
      };
    });

    if (!this.tabs.length) return;

    this.registerSlideshowEvents();
    this.initCarousel();
    this.startSlideshow();
  }

  initCarousel() {
    const $this = this;
    const thumbs = this.querySelector(this.SELECTORS.slideshowThumbsMobile);
    const carousel = this.querySelector(this.SELECTORS.slideshowCarouselMobile);

    if (!thumbs || !carousel || typeof Swiper === 'undefined') return;

    this.swiperThumbs = new Swiper(thumbs, {
      loop: true,
      slideToClickedSlide: true,
      slidesPerView: 2.5,
      allowTouchMove: true,
      spaceBetween: 10,
      centeredSlides: false,
    });

    this.swiperCarousel = new Swiper(carousel, {
      loop: true,
      slidesPerView: 1,
      effect: 'fade',
    });

    this.swiperCarousel.on('slideChange', function (e) {
      const activeSlide = this.slides[this.activeIndex];
      if (activeSlide && activeSlide.dataset.tabId) {
        $this.openTab(activeSlide.dataset.tabId, false);
      }
    });

    this.swiperThumbs.on('slideChange', function (e) {
      $this.swiperCarousel.slideTo(e.realIndex);
    });
  }

  startSlideshow() {
    const currentTab = this.activeTabPanel;
    const currentVideo = this.getVideo(currentTab);

    if (currentVideo) {
      this.playVideo(currentVideo);
    }

    this.scheduleNextTab();
  }

  destroyPanelEvents() {
    this.tabs.forEach((tab) => {
      const currentVideo = this.getVideo(tab.tabPanel);

      if (currentVideo) {
        currentVideo.pause();
      }
    });
  }

  registerSlideshowEvents() {
    this.tabs.forEach((tab) => {
      tab.tabHeaderDesktop.addEventListener('click', (event) => this.handleTabDesktopClick(event, tab));
    });

    this.addEventListener('click', this.boundHandleMobileTabClick);
  }

  handleTabDesktopClick(event, tab) {
    this.openTab(tab.tabId);
  }

  handleMobileTabClick(event) {
    const tabHeader = event.target.closest(this.SELECTORS.tabHeaderMobile);
    if (!tabHeader || !this.contains(tabHeader)) return;

    event.preventDefault();
    this.openTab(tabHeader.dataset.tabId);
  }

  openTab(tabId, shouldUpdateMobileTabHeader = true) {
    this.clearAutoplayTimer();

    const currentTab = this.tabs.find((tab) => tab.tabId === tabId);
    if (!currentTab || !currentTab.tabPanel) return;

    const tabVideo = this.getVideo(currentTab.tabPanel) || null;

    this.hideTabs(currentTab.tabPanel);
    this.updateTabHeaders(currentTab, shouldUpdateMobileTabHeader);
    currentTab.tabPanel.classList.add(this.CLASSES.active);

    if (tabVideo) {
      this.playVideo(tabVideo);
    }

    this.scheduleNextTab();
  }

  hideTabs(lastTabPanel) {
    this.tabs.forEach((tab) => {
      tab.tabHeaderDesktop.classList.remove(this.CLASSES.active);
      tab.tabHeaderDesktop.parentElement.classList.remove(this.CLASSES.active);

      tab.tabPanel.classList.remove(this.CLASSES.active);
      if (this.getVideo(tab.tabPanel)) {
        this.getVideo(tab.tabPanel).pause();
      }
    });
  }

  goToNextTab() {
    if (!this.autoplay || this.tabs.length < 2) return;

    const activeTabHeader = this.activeTabHeader;
    if (!activeTabHeader) return;

    const currentTab = this.tabs.find((tab) => tab.tabId === activeTabHeader.dataset.tabId);
    if (!currentTab) return;

    const currentIndex = this.tabs.indexOf(currentTab);
    const nextIndex = (currentIndex + 1) % this.tabs.length;
    const nextTab = this.tabs[nextIndex];

    this.openTab(nextTab.tabId, true);
  }

  updateTabHeaders(currentTab, shouldUpdateMobileTabHeader = true) {
    const desktopTabHeaderContainer = this.querySelector(this.SELECTORS.desktopTabHeaderContainer);
    const tabHeader = currentTab.tabHeaderDesktop;
    const mobileTabTitle = this.querySelector('.js-mobile-tab-title');
    const mobileTabText = this.querySelector('.js-mobile-tab-text');
    const mobileTabContent = this.querySelector('.js-mobile-tab-content');
    const titleSource = tabHeader.parentElement.querySelector('[data-tab-title]');
    const textSource = tabHeader.parentElement.querySelector('[data-tab-text]');

    currentTab.tabHeaderDesktop.classList.add(this.CLASSES.active);
    currentTab.tabHeaderDesktop.parentElement.classList.add(this.CLASSES.active);

    if (!desktopTabHeaderContainer || !mobileTabContent) return;

    mobileTabContent.classList.add(this.CLASSES.loading);

    if (shouldUpdateMobileTabHeader && this.swiperThumbs && this.swiperCarousel && currentTab.tabHeaderMobile) {
      this.swiperThumbs.slideTo(currentTab.tabHeaderMobile.dataset.swiperSlideIndex);
      this.swiperCarousel.slideTo(currentTab.tabHeaderMobile.dataset.swiperSlideIndex);
    }

    desktopTabHeaderContainer.style.minHeight = desktopTabHeaderContainer.offsetHeight + 'px';
    desktopTabHeaderContainer.classList.add(this.CLASSES.loading);

    desktopTabHeaderContainer.scrollTo({
      left: 0,
      behavior: 'smooth',
    });

    if (!titleSource && !textSource) {
      mobileTabContent.classList.remove(this.CLASSES.loading);
      desktopTabHeaderContainer.classList.remove(this.CLASSES.loading);
      desktopTabHeaderContainer.style.minHeight = 'auto';
      return;
    }

    setTimeout(() => {
      if (mobileTabTitle && titleSource) {
        mobileTabTitle.textContent = titleSource.textContent;
      }

      if (mobileTabText && textSource) {
        mobileTabText.textContent = textSource.textContent;
      }

      setTimeout(() => {
        mobileTabContent.classList.remove(this.CLASSES.loading);
        desktopTabHeaderContainer.classList.remove(this.CLASSES.loading);
        setTimeout(() => {
          desktopTabHeaderContainer.style.minHeight = 'auto';
        }, 500);
      }, 250);
    }, 250);
  }

  getVideo(panel) {
    if (!panel) return null;

    return panel.querySelector('[data-video-player]');
  }

  playVideo(video) {
    if (video.readyState > 0) {
      video.currentTime = 0;
    }

    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        this.scheduleNextTab();
      });
    }
  }

  scheduleNextTab() {
    if (!this.autoplay || this.tabs.length < 2) return;

    this.clearAutoplayTimer();
    this.autoplayTimer = setTimeout(() => {
      this.goToNextTab();
    }, this.autoplayInterval);
  }

  clearAutoplayTimer() {
    if (!this.autoplayTimer) return;

    clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  get tabHeaders() {
    return this.querySelectorAll(this.SELECTORS.tabHeaderDesktop);
  }

  get tabPanels() {
    return this.querySelectorAll(this.SELECTORS.tabPanel);
  }

  get activeTabHeader() {
    return this.querySelector(this.SELECTORS.tabHeaderDesktop + '.active');
  }

  get activeTabPanel() {
    return this.querySelector(this.SELECTORS.tabPanel + '.active');
  }

  get autoplayInterval() {
    return Number(this.getAttribute('data-autoplay-interval')) || 10000;
  }

  get autoplay() {
    return this.getAttribute('data-autoplay') === 'true' || false;
  }
}
customElements.define('slideshow-tabs', SlideshowTabs);
